import React, { useEffect, useMemo, useRef, useState } from "react";
import { HKPImage } from "../../../media/HKPImage";
import { fetchPickerProducts, PickerProduct, PickerQuery } from "../../../components/products/productSelection";
import { useProductPages } from "../../../components/products/useProductPages";
import { getApi } from "../../../helpers/auth_helpers";
import { ALL_CATEGORIES_LIMIT, CategoryOption, toCategoryOptions } from "../../products/categoryFilter";
import { isMediaReference, pickProductListThumbUrl } from "../../products/previewMedia";
import { hasPhoto, heldElsewhere, photoProps } from "./salesPhoneModel";
import { formatInr } from "../salesInvoice";

/** The card-size copy the list API signed; else the media-service id of the first shown photo (fetched lazily). */
const coverOf = (product: PickerProduct) => {
  const media: any[] = Array.isArray(product.thumbnailDetails?.media) ? product.thumbnailDetails.media : [];
  const url = pickProductListThumbUrl(media, "PRODUCT_CARD") || (product.thumbnail ? String(product.thumbnail) : "");
  if (url) return photoProps(url);
  const row = media.find((item) => String(item?.show ?? "1") !== "0" && (item?.mediaAssetId || isMediaReference(item?.mediaURL)));
  return row ? photoProps(row.mediaURL, row.mediaAssetId) : {};
};

const studioName = () => {
  try {
    return String(JSON.parse(localStorage.getItem("authUser") || "null")?.users?.organizationResponse?.name || "").trim().toLowerCase();
  } catch {
    return "";
  }
};

const priceOf = (product: PickerProduct) => {
  const amount = Number(String(product.sellPrice ?? "").replace(/[^\d.]/g, ""));
  return Number.isFinite(amount) && amount > 0 && amount < 1e12 ? formatInr(amount).replace(/\.00$/, "") : "";
};

type Option = { label: string; value: string };
type Facet = "type" | "category" | "price";
type PriceBand = { key: string; label: string; min?: number; max?: number };

/** Bands in rupees; lower bound inclusive, upper bound exclusive, so a piece sits in exactly one band. */
export const PRICE_BANDS: PriceBand[] = [
  { key: "", label: "Any price" },
  { key: "u10", label: "Under ₹10,000", max: 10000 },
  { key: "10-25", label: "₹10,000 – ₹25,000", min: 10000, max: 25000 },
  { key: "25-50", label: "₹25,000 – ₹50,000", min: 25000, max: 50000 },
  { key: "50-100", label: "₹50,000 – ₹1,00,000", min: 50000, max: 100000 },
  { key: "100+", label: "₹1,00,000 and above", min: 100000 },
];

export const priceFilter = (band?: PriceBand) =>
  band && (band.min != null || band.max != null)
    ? {
      minSellPrice: band.min,
      maxSellPrice: band.max,
      minSellPriceInclusive: band.min != null ? true : undefined,
      maxSellPriceInclusive: band.max != null ? false : undefined,
    }
    : undefined;

const base = () => process.env.REACT_APP_SERVERURL || "";
const docsOf = (res: any): any[] => (Array.isArray(res?.data?.docs) ? res.data.docs : []);

type Props = {
  credit: boolean;
  /** productUniqueId of pieces already on this memo. */
  addedIds: Set<string>;
  count: number;
  totalLabel: string;
  /** Returns an error reason to show on the card, or "" when added. */
  onAdd: (product: PickerProduct) => Promise<string>;
  onRemove: (productUniqueId: string) => Promise<void>;
  onScan: () => void;
  onClose: () => void;
};

/** Browse-first piece picker: scroll the studio's pieces, type to narrow, tap to add, tap again to remove. */
export default function SalesPhonePicker({ credit, addedIds, count, totalLabel, onAdd, onRemove, onScan, onClose }: Props) {
  const [text, setText] = useState("");
  // Sold pieces and pieces held on other memos stay out of the way unless the user asks to see them.
  const [availableOnly, setAvailableOnly] = useState(true);
  const [type, setType] = useState<Option | null>(null);
  const [category, setCategory] = useState<CategoryOption | null>(null);
  const [band, setBand] = useState<PriceBand>(PRICE_BANDS[0]);
  const [facet, setFacet] = useState<Facet | "">("");
  const [types, setTypes] = useState<Option[]>([]);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [priceReady, setPriceReady] = useState(false);
  const [pending, setPending] = useState<string>("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const sentinel = useRef<HTMLDivElement | null>(null);

  const query: PickerQuery = useMemo(() => ({
    filters: {
      q: text.trim() || undefined,
      // A credit note looks for sold pieces; a memo for pieces still with the studio.
      inStock: credit ? { value: "false" } : availableOnly ? { value: "true" } : undefined,
      includeAiSuggestions: true,
      type: type || undefined,
      category: category || undefined,
      price: priceReady ? priceFilter(band) : undefined,
    },
    sort: "updatedAt:-1",
    exact: "",
  }), [band, category, credit, availableOnly, priceReady, text, type]);

  useEffect(() => {
    let live = true;
    getApi(`${base()}/type/types?offset=0&limit=50`)
      .then((res: any) => live && setTypes(docsOf(res).map((row) => ({ label: row.name, value: row._id })).filter((row) => row.label)))
      .catch(() => undefined);
    // Price bands need the numeric price backfill; the server says when they are safe to use.
    getApi(`${base()}/product/price-filter-status`)
      .then((res: any) => live && setPriceReady(res?.success === true && res?.data?.enabled === true))
      .catch(() => undefined);
    return () => { live = false; };
  }, []);

  useEffect(() => {
    let live = true;
    // With a type: its categories by id. Without: every category once, matched by name across types.
    const params = new URLSearchParams({ offset: "0", limit: String(ALL_CATEGORIES_LIMIT) });
    if (type) params.set("typeId", type.value);
    getApi(`${base()}/category/categories?${params}`)
      .then((res: any) => live && setCategories(toCategoryOptions(docsOf(res), !type)))
      .catch(() => live && setCategories([]));
    return () => { live = false; };
  }, [type]);

  const pickType = (next: Option | null) => {
    setType(next);
    setCategory(null);
    setFacet("");
  };
  const filtered = Boolean(type || category || band.key);
  const { products: page, loading, error, total, more, next } = useProductPages(query, fetchPickerProducts);
  // Out of stock is not the same as sold: a credit note only offers pieces now owned by another party.
  // Pieces on this memo, or tapped here, stay in the grid even though the list reports them as held.
  const seen = useRef(new Set<string>());
  addedIds.forEach((id) => seen.current.add(id));
  const products = useMemo(() => {
    const studio = studioName();
    return page.filter((product) => {
      if (availableOnly && heldElsewhere(product, seen.current)) return false;
      if (!credit) return true;
      const owner = String(product.productOwner || "").trim().toLowerCase();
      return owner && owner !== studio;
    });
  }, [availableOnly, credit, page, addedIds]);
  const hidden = page.length - products.length;

  useEffect(() => {
    const node = sentinel.current;
    if (!node || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) void next();
    }, { rootMargin: "400px" });
    observer.observe(node);
    return () => observer.disconnect();
  }, [next, products.length, more]);

  const toggle = async (product: PickerProduct) => {
    const id = product.productUniqueId;
    if (pending) return;
    setPending(id);
    setErrors((prior) => ({ ...prior, [id]: "" }));
    try {
      if (addedIds.has(id)) {
        await onRemove(id);
      } else {
        const reason = await onAdd(product);
        if (reason) setErrors((prior) => ({ ...prior, [id]: reason }));
      }
    } finally {
      setPending("");
    }
  };

  return (
    <div className="sp-pick" role="dialog" aria-modal="true" aria-label="Add pieces">
      <div className="sp-pick__head">
        <button type="button" className="sp-back" aria-label="Close" onClick={onClose}>✕</button>
        <h2>{credit ? "Find returned pieces" : "Add pieces"}</h2>
        <button type="button" className="sp-link" onClick={onScan}>⌗ Scan</button>
      </div>
      <div className="sp-pick__filters">
        <input className="sp-search" type="search" value={text} placeholder="Type to filter: name, colour, tag"
          aria-label="Filter pieces" onChange={(e) => setText(e.target.value)} />
        <div className="sp-chips">
          {!credit ? (
            <button type="button" className={`sp-chip${availableOnly ? " is-on" : ""}`} aria-pressed={availableOnly}
              onClick={() => setAvailableOnly((on) => !on)}>{availableOnly ? "✓ " : ""}Available only</button>
          ) : null}
          {types.length > 1 || type ? (
            <button type="button" className={`sp-chip${type ? " is-on" : ""}`} onClick={() => setFacet("type")}>
              {type ? type.label : "Type"} ▾
            </button>
          ) : null}
          <button type="button" className={`sp-chip${category ? " is-on" : ""}`} onClick={() => setFacet("category")}>
            {category ? category.label : "Category"} ▾
          </button>
          {priceReady ? (
            <button type="button" className={`sp-chip${band.key ? " is-on" : ""}`} onClick={() => setFacet("price")}>
              {band.key ? band.label : "Price"} ▾
            </button>
          ) : null}
          {filtered ? (
            <button type="button" className="sp-chip" onClick={() => { pickType(null); setBand(PRICE_BANDS[0]); }}>Clear</button>
          ) : null}
          <span className="t13 sp-pick__count">{loading && !products.length ? "" : credit ? "" : `${Math.max(0, total - hidden)} ${total - hidden === 1 ? "piece" : "pieces"}`}</span>
        </div>
      </div>
      <div className="sp-pick__grid">
        {products.map((product) => {
          const id = product.productUniqueId;
          const added = addedIds.has(id);
          const cover = coverOf(product);
          const unavailable = added ? "" : heldElsewhere(product, seen.current) ? "On another memo"
            : !credit && product.inStock === false ? "Sold" : "";
          return (
            <button key={id} type="button" className={`sp-tile${added ? " is-added" : ""}${unavailable ? " is-off" : ""}`} aria-pressed={added}
              disabled={pending === id} onClick={() => toggle(product)}>
              <span className="sp-tile__photo">
                {hasPhoto(cover) ? <HKPImage preset="PRODUCT_CARD" {...cover} alt="" loading="lazy" sizes="50vw" /> : null}
                {unavailable ? <span className="sp-tile__flag">{unavailable}</span> : null}
                <span className="sp-tile__mark" aria-hidden="true">{pending === id ? "…" : added ? "✓" : "+"}</span>
              </span>
              <span className="sp-tile__name">{product.name || "Untitled piece"}</span>
              <span className="t13 clip">{[priceOf(product), product.color].filter(Boolean).join(" · ") || " "}</span>
              {errors[id] ? <span className="sp-tile__err">{errors[id]}</span> : null}
            </button>
          );
        })}
      </div>
      {error ? (
        <div className="sp-warn" role="alert">{error} <button type="button" className="sp-link" onClick={() => next()}>Retry</button></div>
      ) : null}
      {!loading && !error && products.length === 0 ? (
        <p className="t13 sp-empty">{text || filtered ? `Nothing matches${text ? ` “${text}”` : ""}${filtered ? " with these filters" : ""}.` : credit ? "No sold pieces found." : availableOnly ? "No pieces available. Turn off Available only to see sold pieces and pieces on other memos." : "No pieces yet."}</p>
      ) : null}
      {loading ? <p className="t13 sp-empty" role="status">Loading pieces…</p> : null}
      {more ? <div ref={sentinel} className="sp-pick__more" /> : null}
      {facet ? (
        <>
          <button type="button" className="sp-dim is-top" aria-label="Close" onClick={() => setFacet("")} />
          <div className="sp-sheet is-top" role="dialog" aria-modal="true" aria-label={`Filter by ${facet}`}>
            <div className="sp-grab" />
            <h2>{facet === "type" ? "Type" : facet === "category" ? "Category" : "Price"}</h2>
            <div className="sp-menu">
              {facet === "type" ? (
                <>
                  <button type="button" aria-pressed={!type} onClick={() => pickType(null)}>{!type ? "✓ " : ""}All types</button>
                  {types.map((option) => (
                    <button key={option.value} type="button" aria-pressed={type?.value === option.value}
                      onClick={() => pickType(option)}>{type?.value === option.value ? "✓ " : ""}{option.label}</button>
                  ))}
                </>
              ) : facet === "category" ? (
                <>
                  <button type="button" aria-pressed={!category} onClick={() => { setCategory(null); setFacet(""); }}>
                    {!category ? "✓ " : ""}All categories{type ? ` in ${type.label}` : ""}
                  </button>
                  {categories.map((option) => (
                    <button key={option.value} type="button" aria-pressed={category?.value === option.value}
                      onClick={() => { setCategory(option); setFacet(""); }}>
                      {category?.value === option.value ? "✓ " : ""}{option.label}
                    </button>
                  ))}
                  {!categories.length ? <p className="t13">No categories yet.</p> : null}
                </>
              ) : (
                PRICE_BANDS.map((option) => (
                  <button key={option.key || "any"} type="button" aria-pressed={band.key === option.key}
                    onClick={() => { setBand(option); setFacet(""); }}>
                    {band.key === option.key ? "✓ " : ""}{option.label}
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      ) : null}
      <div className="sp-pick__bar">
        <button type="button" className="sp-btn is-pri" onClick={onClose}>
          Done · {count} {count === 1 ? "piece" : "pieces"} · {totalLabel}
        </button>
      </div>
    </div>
  );
}
